import { DatasetJsonLd, WebPageJsonLd } from "next-seo";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import type { ReferenceData } from "~/modules/reference_data/types";
import { getReferenceDataURL } from "~/routes";

// // // //

export function getReferenceDataPageTitle(props: {
    referenceData: ReferenceData;
}): string {
    const { referenceData } = props;
    return `${referenceData.name} | RWA Data Catalog`;
}

export function getReferenceDataPageDescription(props: {
    referenceData: ReferenceData;
}): string {
    const { referenceData } = props;

    if (referenceData.description) {
        return referenceData.description;
    }

    const parts = [
        `Explore the ${referenceData.name} reference data field in the RWA.xyz data catalog.`,
    ];

    if (referenceData.asset_count > 0) {
        parts.push(
            `Covers ${referenceData.asset_count.toLocaleString()} assets`,
        );
    }

    if (referenceData.network_count > 0) {
        parts.push(
            `across ${referenceData.network_count.toLocaleString()} networks`,
        );
    }

    return parts.join(" ") + ".";
}

// // // //

/**
 * Renders WebPage + Dataset + DefinedTerm JSON-LD structured data
 * for the Reference Data Individual page.
 */
export function ReferenceDataIndividualSEO(props: {
    referenceData: ReferenceData;
}) {
    const { referenceData } = props;

    const pageUrl = addBaseUrl(
        getReferenceDataURL({ referenceSlug: referenceData.slug }),
    );
    const description = getReferenceDataPageDescription({ referenceData });

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const isoYesterday = yesterday.toISOString();

    // DefinedTerm JSON-LD: represents a defined concept in a taxonomy
    // Not available as a next-seo primitive, so we render it manually.
    const definedTermJsonLd = {
        "@context": "https://schema.org",
        "@type": "DefinedTerm",
        "@id": pageUrl,
        name: referenceData.name,
        description,
        url: pageUrl,
        inDefinedTermSet: {
            "@type": "DefinedTermSet",
            name: "RWA.xyz Data Catalog",
            url: addBaseUrl("/catalog"),
        },
    };

    return (
        <>
            <WebPageJsonLd
                id={pageUrl}
                description={description}
                lastReviewed={isoYesterday}
                reviewedBy={{
                    type: "Organization",
                    name: "RWA.xyz",
                }}
            />
            <DatasetJsonLd
                name={`${referenceData.name} — RWA.xyz Reference Data`}
                description={description}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(definedTermJsonLd),
                }}
            />
        </>
    );
}
